import React, { useState } from 'react';
import { useLiveMatch } from '../hooks/useLiveMatch';
import { ConfirmModal } from './ConfirmModal';
import { SubstitutionModal } from './SubstitutionModal';
import { CaptainTransferModal } from './CaptainTransferModal';
import { VARReviewModal } from './VARReviewModal';
import { OperatorModeSelector } from './OperatorModeSelector';
import { PhysicalButton } from './broadcast/PhysicalButton';
import { LEDScoreboard } from './broadcast/LEDScoreboard';
import { BroadcastPlayerRow } from './broadcast/BroadcastPlayerRow';
import { AutoHideHeader } from './AutoHideHeader';
import { TimeControlsModal } from './broadcast/TimeControlsModal';
import { ScoreEditModal } from './broadcast/ScoreEditModal';
import { TeamLogo } from './TeamLogo';
import { useLogoStyle } from '../hooks/useLogoStyle';

// Connection Status Light Component
const StatusLight = ({ connected, label }) => {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-3 h-3 rounded-full ${connected ? 'bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.8)]' : 'bg-red-500 shadow-[0_0_10px_rgba(239,68,68,0.8)]'}`}></div>
      <span className="text-xs text-gray-400 uppercase tracking-wide">{label}</span>
    </div>
  );
};

// Giant Timer Component with Ghost Segments
const GiantTimer = ({ minute, second, onClick }) => {
  const formattedTime = `${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;

  return (
    <div
      className={`flex flex-col items-center gap-1 ${onClick ? 'cursor-pointer hover:scale-105 transition-transform' : ''}`}
      onClick={onClick}
      title={onClick ? 'Click to open time controls' : ''}
    >
      <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
        MATCH CLOCK
      </div>
      <div className="relative bg-black rounded-lg p-2 shadow-[inset_0_2px_8px_rgba(0,0,0,0.8)] border border-gray-800">
        {/* Ghost layer - all segments visible */}
        <div
          className="seven-segment text-2xl font-bold text-gray-800 tabular-nums leading-none"
          aria-hidden="true"
        >
          88:88
        </div>
        {/* Active layer - actual time */}
        <div className="absolute inset-0 p-2 seven-segment text-2xl font-bold text-amber-400 shadow-[0_0_15px_rgba(251,191,36,0.5)] tabular-nums leading-none">
          {formattedTime}
        </div>
      </div>
    </div>
  );
};

// Broadcast Header Component
const BroadcastHeader = ({ connected, joined, viewers, match, matchId }) => {
  const getStatusBadge = () => {
    if (!match) return null;
    const statusColors = {
      scheduled: 'bg-blue-900/50 text-blue-400',
      live: 'bg-green-900/50 text-green-400',
      completed: 'bg-gray-700 text-gray-300',
      cancelled: 'bg-red-900/50 text-red-400',
    };
    const colorClass = statusColors[match.status] || statusColors.scheduled;
    return (
      <div className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${colorClass}`}>
        {match.status}
      </div>
    );
  };

  return (
    <div className="h-16 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-b border-amber-600/30 flex items-center justify-between px-6">
      {/* Left: Branding */}
      <div className="flex items-center gap-4">
        <h1 className="text-xl font-bold text-amber-400 tracking-wide">
          SPORTS MANAGER <span className="text-gray-500">|</span> <span className="text-white">Broadcast</span>
        </h1>
        {getStatusBadge()}
      </div>

      {/* Center: Status Indicators */}
      <div className="flex items-center gap-6">
        <StatusLight connected={connected} label="WebSocket" />
        <StatusLight connected={joined} label="Room" />
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.8)]"></div>
          <span className="text-xs text-gray-400 uppercase tracking-wide">Viewers: {viewers}</span>
        </div>
      </div>

      {/* Right: Mode Selector */}
      <div>
        <OperatorModeSelector currentMode="broadcast" matchId={matchId} />
      </div>
    </div>
  );
};

// Top Control Bar Component - Timer and Overlay Controls
const TopControlBar = ({ m, onOpenTimeControls }) => {
  return (
    <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-b border-amber-600/30 px-6 py-3">
      <div className="flex items-center justify-center gap-8">
        {/* Compact Clickable Timer */}
        <GiantTimer
          minute={m.clock.minute}
          second={m.clock.second}
          onClick={onOpenTimeControls}
        />

        {/* Overlay Toggle Controls */}
        <div className="flex items-center gap-2">
          <PhysicalButton
            color="blue"
            size="tiny"
            active={m.scoreboardVisible}
            onClick={m.toggleScoreboard}
          >
            Score
          </PhysicalButton>
          <PhysicalButton
            color="blue"
            size="tiny"
            active={m.formationsVisible}
            onClick={m.toggleFormations}
          >
            Form
          </PhysicalButton>
          <PhysicalButton
            color="blue"
            size="tiny"
            active={m.logoVisible}
            onClick={m.toggleLogo}
          >
            Logo
          </PhysicalButton>
        </div>
      </div>
    </div>
  );
};

// Team Panel Component
const TeamPanel = ({ players, teamInfo, label, isHome, m, score, onScoreClick, logoStyle }) => {
  if (!players || players.length === 0) {
    return (
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg border border-gray-700 p-4">
        <div className="text-center text-gray-500">No team data</div>
      </div>
    );
  }

  const activePlayers = players.filter(p => p.eligibility?.eligible && !p.eligibility?.isExpelled && !p.eligibility?.isSubstituted);
  const activeCount = activePlayers.length;
  const totalCount = players.filter(p => p.is_starting).length;

  const handleEventCreated = (eventType, playerPos) => {
    if (eventType === 'substitution') {
      m.setSubstitutionModal({
        isOpen: true,
        team: isHome ? 'home' : 'away',
        outgoingPlayer: playerPos,
      });
    } else {
      m.handleQuickEvent(eventType, playerPos);
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg border border-gray-700 flex flex-col">
      {/* Header with logo and clickable scoreboard */}
      <div className="p-3 border-b border-gray-700">
        <div className="flex items-center gap-3">
          {/* Team Logo */}
          <TeamLogo team={teamInfo} size="w-12 h-12" logoStyle={logoStyle} shape="circle" />

          {/* Team Info */}
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">
              {label}
            </div>
            <div className="text-base font-bold text-white truncate">
              {teamInfo?.name || label}
            </div>
            <div className="text-xs text-gray-400 font-mono">
              Players: {activeCount}/{totalCount}
            </div>
          </div>

          {/* Clickable Compact Scoreboard */}
          <div
            className="flex-shrink-0 cursor-pointer hover:scale-105 transition-transform"
            onClick={onScoreClick}
            title="Click to edit score"
          >
            <LEDScoreboard
              score={score}
              color={isHome ? 'blue' : 'red'}
              compact={true}
            />
          </div>
        </div>
      </div>

      {/* Player List */}
      <div className="flex-1 overflow-y-auto p-2">
        {players.map((playerPos) => (
          <BroadcastPlayerRow
            key={playerPos.id}
            playerPos={playerPos}
            onEventCreated={handleEventCreated}
          />
        ))}
      </div>
    </div>
  );
};

// Event Log Bar Component
const EventLogBar = ({ events, setVarModal }) => {
  const recentEvents = events.slice(-10);

  const getEventIcon = (eventType) => {
    const icons = {
      goal: '⚽',
      penalty_goal: '⚽',
      own_goal: '⚽',
      yellow_card: '🟨',
      red_card: '🟥',
      substitution: '🔄',
      var_review: '📹',
    };
    return icons[eventType] || '•';
  };

  const getEventColor = (eventType) => {
    const colors = {
      goal: 'text-green-400',
      penalty_goal: 'text-green-400',
      own_goal: 'text-green-400',
      yellow_card: 'text-yellow-400',
      red_card: 'text-red-400',
      substitution: 'text-blue-400',
      var_review: 'text-purple-400',
    };
    return colors[eventType] || 'text-gray-400';
  };

  const canVAR = (event) => {
    return ['goal', 'penalty_goal', 'own_goal', 'penalty', 'red_card'].some(t =>
      event.event_type?.toLowerCase().includes(t)
    ) && !event.is_cancelled;
  };

  return (
    <div className="h-20 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-t border-amber-600/30 px-4 py-2 overflow-x-auto">
      <div className="flex items-center gap-3 h-full">
        <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest whitespace-nowrap">
          Recent Events:
        </div>
        <div className="flex gap-3 overflow-x-auto">
          {recentEvents.length > 0 ? (
            recentEvents.map((event, idx) => (
              <div
                key={event.id || idx}
                className="flex items-center gap-2 bg-gray-800/50 rounded px-3 py-2 whitespace-nowrap"
              >
                <span className="text-lg">{getEventIcon(event.event_type)}</span>
                <span className={`text-sm font-medium ${getEventColor(event.event_type)}`}>
                  {event.minute}' - {event.description || event.event_type}
                </span>
                {canVAR(event) && (
                  <button
                    onClick={() => setVarModal({ isOpen: true, event })}
                    className="ml-2 px-2 py-1 bg-purple-900/50 text-purple-400 text-xs font-bold rounded hover:bg-purple-800/50 transition-colors"
                  >
                    VAR
                  </button>
                )}
              </div>
            ))
          ) : (
            <div className="text-sm text-gray-500">No events yet</div>
          )}
        </div>
      </div>
    </div>
  );
};

// Connection Screen Component
const ConnectionScreen = () => {
  return (
    <div className="h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center">
      <div className="text-center">
        <div className="text-4xl font-bold text-amber-400 mb-4">SPORTS MANAGER</div>
        <div className="text-xl text-gray-400 mb-8">Broadcast Control</div>
        <div className="flex items-center justify-center gap-3">
          <div className="w-4 h-4 rounded-full bg-amber-500 animate-pulse shadow-[0_0_20px_rgba(245,158,11,0.8)]"></div>
          <div className="text-lg text-gray-300">CONNECTING...</div>
        </div>
      </div>
    </div>
  );
};

// Main Component
export const LiveMatchViewBroadcast = ({ matchId }) => {
  const m = useLiveMatch(matchId);
  const [timeControlsModal, setTimeControlsModal] = useState(false);
  const [scoreEditModal, setScoreEditModal] = useState(false);
  const { logoStyle } = useLogoStyle();

  if (!m.connected) {
    return <ConnectionScreen />;
  }

  const handleScoreUpdate = (homeScore, awayScore) => {
    m.handleScoreUpdate(homeScore, awayScore);
  };

  return (
    <>
      <div className="h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex flex-col">
        {/* Auto-hide header for iPad/touch usage */}
        <AutoHideHeader timeout={5000}>
          <BroadcastHeader
            connected={m.connected}
            joined={m.joined}
            viewers={m.viewers}
            match={m.match}
            matchId={matchId}
          />
        </AutoHideHeader>

        {/* Top Control Bar - Timer and Overlay Controls */}
        <TopControlBar
          m={m}
          onOpenTimeControls={() => setTimeControlsModal(true)}
        />

        {/* Two-column layout for teams - wider columns */}
        <div className="flex-1 grid grid-cols-2 gap-3 p-3 overflow-hidden">
          <TeamPanel
            players={m.homeTeam}
            teamInfo={m.match?.home_team}
            label="HOME"
            isHome={true}
            m={m}
            score={m.score.home}
            onScoreClick={() => setScoreEditModal(true)}
            logoStyle={logoStyle}
          />
          <TeamPanel
            players={m.awayTeam}
            teamInfo={m.match?.away_team}
            label="AWAY"
            isHome={false}
            m={m}
            score={m.score.away}
            onScoreClick={() => setScoreEditModal(true)}
            logoStyle={logoStyle}
          />
        </div>
        <EventLogBar events={m.events} setVarModal={m.setVarModal} />
      </div>

      {/* Reuse existing modals */}
      <ConfirmModal
        isOpen={m.confirmModal.isOpen}
        title={m.confirmModal.title}
        message={m.confirmModal.message}
        onConfirm={m.confirmModal.onConfirm}
        onCancel={() => m.setConfirmModal({ isOpen: false })}
      />

      <SubstitutionModal
        isOpen={m.substitutionModal.isOpen}
        outgoingPlayer={m.substitutionModal.outgoingPlayer}
        benchPlayers={m.benchPlayersForSubstitution}
        onConfirm={m.handleSubstitutionConfirm}
        onCancel={() => m.setSubstitutionModal({ isOpen: false, outgoingPlayer: null })}
      />

      <CaptainTransferModal
        isOpen={m.captainTransferModal.isOpen}
        currentCaptain={m.captainTransferModal.currentCaptain}
        eligiblePlayers={m.eligiblePlayersForCaptain}
        onConfirm={(newCaptainId) => {
          m.handleCaptainTransfer(newCaptainId);
          m.setCaptainTransferModal({ isOpen: false });
        }}
        onCancel={() => m.setCaptainTransferModal({ isOpen: false })}
      />

      <VARReviewModal
        isOpen={m.varModal.isOpen}
        event={m.varModal.event}
        matchId={matchId}
        onClose={() => m.setVarModal({ isOpen: false, event: null })}
      />

      <TimeControlsModal
        isOpen={timeControlsModal}
        onClose={() => setTimeControlsModal(false)}
        clock={m.clock}
        onStartClock={m.handleStartClock}
        onStopClock={m.stopClock}
        onResetClock={m.resetClock}
        onChangeHalf={m.changeHalf}
        onSetInjuryTime={m.setInjuryTime}
        match={m.match}
        onStartMatch={m.handleStartMatch}
        onEndMatch={m.handleEndMatch}
        onCancelMatch={m.handleCancelMatch}
      />

      <ScoreEditModal
        isOpen={scoreEditModal}
        onClose={() => setScoreEditModal(false)}
        homeTeamName={m.match?.home_team?.name || 'HOME'}
        awayTeamName={m.match?.away_team?.name || 'AWAY'}
        currentHomeScore={m.score.home}
        currentAwayScore={m.score.away}
        onUpdateScore={handleScoreUpdate}
        events={m.events}
        createEvent={m.createEvent}
        deleteEvent={m.deleteEvent}
        eventTypes={m.eventTypes}
        clock={m.clock}
        homeLineup={m.homeTeam}
        awayLineup={m.awayTeam}
        matchId={matchId}
        match={m.match}
      />
    </>
  );
};
